import type {AuthorizedFetch} from "./client";
import type {components} from "./generated";

export type AutonomousWorkRequest = components["schemas"]["AutonomousWorkRequest"];
export type AutonomousPolicy = components["schemas"]["AutonomousPolicy"];
export type AutonomousDashboard = components["schemas"]["AutonomousFactoryDashboard"];
export type AutonomousSimulation = components["schemas"]["AutonomousSimulation"];
export type AutonomousEffectivePolicy = components["schemas"]["AutonomousEffectivePolicy"];
export type AutonomousPolicyBacktest = components["schemas"]["AutonomousPolicyBacktest"];
export type AutonomousPolicyBacktestRequest = components["schemas"]["AutonomousPolicyBacktestRequest"];

export async function readAutonomousDashboard(
  fetcher: AuthorizedFetch,
  signal?: AbortSignal
): Promise<AutonomousDashboard> {
  return await read(fetcher, "/api/v1/autonomous-factory/dashboard", signal);
}

export async function listAutonomousWork(
  fetcher: AuthorizedFetch,
  signal?: AbortSignal
): Promise<AutonomousWorkRequest[]> {
  return await read(fetcher, "/api/v1/autonomous-factory/work-requests", signal);
}

export async function listAutonomousPolicies(
  fetcher: AuthorizedFetch,
  signal?: AbortSignal
): Promise<AutonomousPolicy[]> {
  return await read(fetcher, "/api/v1/autonomous-factory/policies", signal);
}

export async function previewEffectivePolicy(
  fetcher: AuthorizedFetch,
  scopes: Record<string, string>,
  requestOverrides: Record<string, unknown> = {}
): Promise<AutonomousEffectivePolicy> {
  const response = await fetcher("/api/v1/autonomous-factory/policies/effective", {
    method: "POST",
    headers: {Accept: "application/json", "Content-Type": "application/json"},
    body: JSON.stringify({scopes, requestOverrides})
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Effective policy preview failed: ${response.status}`);
  }
  return await response.json() as AutonomousEffectivePolicy;
}

export async function backtestAutonomousPolicy(
  fetcher: AuthorizedFetch,
  request: AutonomousPolicyBacktestRequest
): Promise<AutonomousPolicyBacktest> {
  const response = await fetcher("/api/v1/autonomous-factory/policies/backtest", {
    method: "POST",
    headers: {Accept: "application/json", "Content-Type": "application/json"},
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Policy backtest failed: ${response.status}`);
  }
  return await response.json() as AutonomousPolicyBacktest;
}

async function read<T>(fetcher: AuthorizedFetch, path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetcher(path, {signal, headers: {Accept: "application/json"}});
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Autonomous Factory request failed: ${response.status}`);
  }
  return await response.json() as T;
}
